/**
 * CLIENT-side video preflight (F3-F): before uploading, checks that the
 * browser can decode the file (hidden `<video>` + `loadedmetadata`) and
 * captures the DURATION and a POSTER (canvas at t=1s → JPEG). Everything
 * with object URLs revoked when done. If decoding fails, the upload is
 * rejected with a clear message (unsupported format).
 */

export interface VideoProbeResult {
  durationSeconds: number;
  /** Captured JPEG poster, or null if it couldn't be captured (e.g. protected video). */
  poster: Blob | null;
  width: number;
  height: number;
}

/** Reason for the preflight failure (the UI translates it; no text in the lib). */
export type VideoDecodeReason = "decode" | "timeout";

export class VideoDecodeError extends Error {
  constructor(readonly reason: VideoDecodeReason) {
    super(reason);
    this.name = "VideoDecodeError";
  }
}

const POSTER_TIME_SECONDS = 1;
const POSTER_QUALITY = 0.82;
const PROBE_TIMEOUT_MS = 15_000;

/**
 * Checks decoding + captures duration and poster. Throws `VideoDecodeError`
 * if the browser can't play the file.
 */
export function probeVideo(file: File): Promise<VideoProbeResult> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.preload = "metadata";
    video.muted = true;
    video.playsInline = true;
    video.src = url;

    let settled = false;
    const cleanup = () => {
      URL.revokeObjectURL(url);
      video.removeAttribute("src");
      video.load();
    };
    const fail = (reason: VideoDecodeReason) => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(timer);
      cleanup();
      reject(new VideoDecodeError(reason));
    };
    const succeed = (result: VideoProbeResult) => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(timer);
      cleanup();
      resolve(result);
    };

    const timer = setTimeout(() => fail("timeout"), PROBE_TIMEOUT_MS);

    video.addEventListener("error", () => fail("decode"));

    video.addEventListener("loadedmetadata", () => {
      const durationSeconds = Number.isFinite(video.duration) ? video.duration : 0;
      const width = video.videoWidth;
      const height = video.videoHeight;
      // Seek to the poster frame; once the seek completes it's drawn to the canvas.
      const seekTarget = Math.min(POSTER_TIME_SECONDS, Math.max(0, durationSeconds - 0.1));
      video.addEventListener(
        "seeked",
        () => {
          void capturePoster(video, width, height).then((poster) =>
            succeed({ durationSeconds, poster, width, height }),
          );
        },
        { once: true },
      );
      try {
        video.currentTime = seekTarget;
      } catch {
        succeed({ durationSeconds, poster: null, width, height });
      }
    });
  });
}

async function capturePoster(
  video: HTMLVideoElement,
  width: number,
  height: number,
): Promise<Blob | null> {
  if (width === 0 || height === 0) {
    return null;
  }
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return null;
  }
  try {
    ctx.drawImage(video, 0, 0, width, height);
  } catch {
    // CORS-tainted video or protected origin → no poster.
    return null;
  }
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), "image/jpeg", POSTER_QUALITY);
  });
}

/** Extensions/MIME accepted by the file input (mp4/webm v1). */
export const ACCEPTED_VIDEO_TYPES = "video/mp4,video/webm";

/** Does the file have an accepted video type (mp4/webm)? */
export function isAcceptedVideoType(file: File): boolean {
  return file.type === "video/mp4" || file.type === "video/webm";
}
